/**
 * TypeOverviewHero — type-specific hero card rendered above the shared
 * fund summary on the Overview tab. Each type gets its most-relevant
 * single signal in one card (no scrolling):
 *
 *   Sports → Next session / streak
 *   Trip   → Trip phase + countdown
 *   Event  → Budget gauge (collected vs target)
 *   Daily  → This month spend + recurring due this week
 *
 * If the type has no special signal (or backend data not available),
 * the component renders nothing — safe to always mount.
 */
import { useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Calendar, Plane, PartyPopper, Home, Flame } from 'lucide-react';
import { formatNXC } from '../lib/formatNXC';

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function SportsHero({ group, fundData }) {
  // "Next session" comes from a sessions array if backend has hydrated it. Otherwise
  // fall back to month-momentum count from fund snapshot.
  const sessions = group?.sessions || [];
  const upcoming = sessions
    .filter((s) => s?.session_start && new Date(s.session_start) > new Date())
    .sort((a, b) => new Date(a.session_start) - new Date(b.session_start))[0];

  if (upcoming) {
    const days = daysUntil(upcoming.session_start);
    const label = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `in ${days} days`;
    return (
      <Card className="p-4 rounded-2xl border-2 border-emerald-500/30 bg-emerald-500/5" data-testid="sports-next-session-hero">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
            <Calendar className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase font-bold tracking-wider text-muted-foreground">Next session</p>
            <p className="font-display font-bold truncate">{upcoming.title || 'Match'}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        </div>
      </Card>
    );
  }

  // No upcoming session — show monthly fines + sessions count as a placeholder.
  const finesThisMonth = Math.round(fundData?.total_fines_collected || 0);
  if (finesThisMonth <= 0) return null;
  return (
    <Card className="p-4 rounded-2xl border-2 border-emerald-500/30 bg-emerald-500/5" data-testid="sports-fines-hero">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
          <Flame className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase font-bold tracking-wider text-muted-foreground">Fines this period</p>
          <p className="font-display text-2xl font-bold">{formatNXC(finesThisMonth)}</p>
        </div>
      </div>
    </Card>
  );
}

function TripHero({ group }) {
  const start = group?.trip_start_date;
  const end = group?.trip_end_date;
  if (!start && !end) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startD = start ? new Date(start) : null;
  const endD = end ? new Date(end) : null;
  let phase = 'pre', label = '', highlight = '';
  if (startD && today < startD) {
    phase = 'pre';
    label = 'Trip starts';
    const days = daysUntil(start);
    highlight = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `in ${days} days`;
  } else if (startD && endD && today >= startD && today <= endD) {
    phase = 'live';
    label = 'Trip is live';
    const days = daysUntil(end);
    highlight = days === 0 ? 'Ends today' : `${days} day(s) left`;
  } else if (endD && today > endD) {
    phase = 'post';
    label = 'Trip ended';
    highlight = 'Time to settle up';
  }
  const tones = {
    pre: 'border-sky-500/30 bg-sky-500/5 text-sky-700 dark:text-sky-300',
    live: 'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300',
    post: 'border-violet-500/30 bg-violet-500/5 text-violet-700 dark:text-violet-300',
  };

  return (
    <Card className={`p-4 rounded-2xl border-2 ${tones[phase]}`} data-testid={`trip-phase-${phase}-hero`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-current/20 flex items-center justify-center shrink-0 opacity-90">
          <Plane className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase font-bold tracking-wider opacity-80">{label}</p>
          <p className="font-display text-xl font-bold">{highlight}</p>
        </div>
      </div>
    </Card>
  );
}

function EventHero({ group, fundData }) {
  const target = group?.target_budget;
  const eventDate = group?.event_date;
  const collected = Math.round(fundData?.total_contributions || 0);
  if (!target && !eventDate) return null;

  const pct = target ? Math.min(100, Math.round((collected / target) * 100)) : null;
  const colour = pct == null ? 'bg-amber-500' : pct >= 100 ? 'bg-emerald-500' : pct >= 90 ? 'bg-yellow-500' : 'bg-amber-500';
  const days = eventDate ? daysUntil(eventDate) : null;

  return (
    <Card className="p-4 rounded-2xl border-2 border-amber-500/30 bg-amber-500/5" data-testid="event-budget-hero">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
          <PartyPopper className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase font-bold tracking-wider text-muted-foreground">Event</p>
          {eventDate && (
            <p className="font-display text-base font-bold">
              {days == null ? '' : days < 0 ? 'Already happened' : days === 0 ? 'Today!' : days === 1 ? 'Tomorrow' : `in ${days} days`}
            </p>
          )}
        </div>
      </div>
      {target ? (
        <>
          <div className="flex items-end justify-between mb-1">
            <span className="text-xs text-muted-foreground">Collected</span>
            <span className="text-xs font-bold tabular-nums">{formatNXC(collected)} / {formatNXC(target)}</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className={`h-full ${colour} transition-all`} style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">{pct}% of target</p>
        </>
      ) : null}
    </Card>
  );
}

function DailyHero({ fundSnapshot }) {
  if (!fundSnapshot) return null;
  const monthLabel = fundSnapshot.month_label || 'This Month';
  const outflow = Math.round(fundSnapshot.outflow || 0);
  const inflow = Math.round(fundSnapshot.inflow || 0);
  return (
    <Card className="p-4 rounded-2xl border-2 border-violet-500/30 bg-violet-500/5" data-testid="daily-month-hero">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-500/20 text-violet-700 dark:text-violet-300 flex items-center justify-center shrink-0">
          <Home className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase font-bold tracking-wider text-muted-foreground">{monthLabel}</p>
          <div className="flex items-baseline gap-3 flex-wrap">
            <p className="font-display text-xl font-bold tabular-nums">−{formatNXC(outflow)} spent</p>
            <p className="text-xs text-muted-foreground tabular-nums">+{formatNXC(inflow)} in</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function TypeOverviewHero({ ctx, typeConfig }) {
  const { group, fundData, fundSnapshot } = ctx || {};

  return useMemo(() => {
    if (!group || !typeConfig) return null;
    if (typeConfig.isSports) return <SportsHero group={group} fundData={fundData} />;
    if (typeConfig.isTrip && typeConfig.features?.trip_phase_banner) return <TripHero group={group} />;
    if (typeConfig.isEvent && typeConfig.features?.event_budget_gauge) return <EventHero group={group} fundData={fundData} />;
    if (typeConfig.isDaily && typeConfig.features?.monthly_view) return <DailyHero fundSnapshot={fundSnapshot} />;
    return null;
  }, [group, fundData, fundSnapshot, typeConfig]);
}
